import { DropContainer } from "./DropContainer";
import { VBox } from "./Box";
import { classNames } from "@/utils/classNames";
import { Icon } from "./Icon";
import { FileUploadConfig, pdfUploadConfig } from "@/fileUpload/fileUploadDomain";
import { SelectFilesButton, SelectFilesButtonProps } from "./SelectFilesButton";
import { LoadingIndicator } from "./LoadingIndicator";

import { useTranslation } from "@/stores/RootStoreContext";
import { observer } from "mobx-react";
import { EmptyState } from "./EmptyState";

export type UploadFilesPanelProps = {
  fileUploadConfig: FileUploadConfig;
  isUploading?: boolean;
  onUpload: SelectFilesButtonProps["onChange"];
  multiple?: boolean;
};

export function UploadFilesPanel({
  fileUploadConfig,
  isUploading,
  onUpload,
  multiple,
}: UploadFilesPanelProps) {
  const { t } = useTranslation();
  return (
    <DropContainer onDrop={onUpload} fileUploadConfig={fileUploadConfig}>
      {({ active }) => (
        <VBox
          bg={active || isUploading ? "alt2" : "alt1"}
          border={active || isUploading ? "primary" : true}
          rounded
          padding
          className={classNames("text-center", (active || isUploading) && "border-2 border-dashed")}
        >
          {isUploading ? (
            <LoadingIndicator size={"md"} />
          ) : active ? (
            <>
              <Icon name={"upload"} color={"info"} className={"align-text-bottom"} />{" "}
              <strong>{t("upload-panel.drop-here-to-start-upload")}</strong>
            </>
          ) : (
            <>
              {t("upload-panel.drag-to-upload-or")}{" "}
              <SelectFilesButton
                className={"align-middle self-center"}
                variant={"link"}
                fileUploadConfig={fileUploadConfig}
                onChange={onUpload}
                multiple={multiple}
              />
            </>
          )}
        </VBox>
      )}
    </DropContainer>
  );
}

export const UploadPdfPlaceholder = observer(
  ({
    onUpload,
    isUploading,
    isConverting,
    fileUploadConfig = pdfUploadConfig,
  }: {
    onUpload?: SelectFilesButtonProps["onChange"];
    isUploading?: boolean;
    isConverting?: boolean;
    fileUploadConfig?: FileUploadConfig;
  }) => {
    const { t } = useTranslation();
    return (
      <DropContainer onDrop={onUpload} fileUploadConfig={fileUploadConfig}>
        {({ active }) => (
          <VBox
            rounded
            bg
            dropShadow={"lg"}
            marginX
            justifyContent={"center"}
            className={classNames(
              "border",
              active ? "border-2" : "border",
              "border-dashed",
              "border-primary",
              active && "bg-alt2",
              "min-h-80",
            )}
          >
            <EmptyState
              maxWidth={"md"}
              illustration={<Icon name={"upload"} size={"xxl"} color={"info"} />}
              description={
                isUploading ? (
                  <LoadingIndicator
                    size={"md"}
                    label={isConverting ? t("attachments.converting-to-pdf") : t("loading")}
                  />
                ) : (
                  <VBox gap={"md"}>
                    <div>{t("upload-panel.drag-to-upload")}</div>
                    <SelectFilesButton
                      className={"align-middle self-center"}
                      fileUploadConfig={fileUploadConfig}
                      onChange={(files) => onUpload?.(files, fileUploadConfig)}
                      multiple={false}
                    />
                  </VBox>
                )
              }
            />
          </VBox>
        )}
      </DropContainer>
    );
  },
);
